import { getCalloutMetrics } from './pinCalloutLayout';

const ALTERNATE_PIN_NAME_FIELDS = [
  'alternate_pin_names',
  'alternatePinNames',
  'alternate_pin_name',
  'alternatePinName',
  'alternate_names',
  'alternateNames',
  'alternate_name',
  'alternateName',
  'pin_aliases',
  'pinAliases',
  'pin_alias',
  'pinAlias',
  'aliases',
  'alias',
  'secondary_pin_names',
  'secondaryPinNames',
  'secondary_pin_name',
  'secondaryPinName',
  'secondary_names',
  'secondaryNames',
  'secondary_name',
  'secondaryName',
  'silkscreen_names',
  'silkscreenNames',
  'silkscreen_name',
  'silkscreenName',
];

export const MAX_PIN_ALTERNATE_NAMES = 4;

export const ALT_PIN_CALLOUT_COLOR_OPTIONS = Object.freeze([
  {
    value: 'pink',
    label: 'Pink',
    fill: '#ec4899',
    stroke: '#be185d',
    text: '#ffffff',
  },
  {
    value: 'orange',
    label: 'Orange',
    fill: '#f97316',
    stroke: '#c2410c',
    text: '#ffffff',
  },
  {
    value: 'yellow',
    label: 'Yellow',
    fill: '#facc15',
    stroke: '#ca8a04',
    text: '#422006',
  },
  {
    value: 'yellow-green',
    label: 'Yellow Green',
    fill: '#a3e635',
    stroke: '#4d7c0f',
    text: '#365314',
  },
  {
    value: 'green',
    label: 'Green',
    fill: '#22c55e',
    stroke: '#15803d',
    text: '#ffffff',
  },
  {
    value: 'sky-blue',
    label: 'Sky Blue',
    fill: '#38bdf8',
    stroke: '#0284c7',
    text: '#082f49',
  },
  {
    value: 'purple',
    label: 'Purple',
    fill: '#a855f7',
    stroke: '#7e22ce',
    text: '#ffffff',
  },
  {
    value: 'blue',
    label: 'Blue',
    fill: '#3b82f6',
    stroke: '#1d4ed8',
    text: '#ffffff',
  },
  {
    value: 'teal',
    label: 'Teal',
    fill: '#14b8a6',
    stroke: '#0f766e',
    text: '#ffffff',
  },
  {
    value: 'indigo',
    label: 'Indigo',
    fill: '#6366f1',
    stroke: '#4338ca',
    text: '#ffffff',
  },
  {
    value: 'magenta',
    label: 'Magenta',
    fill: '#d946ef',
    stroke: '#a21caf',
    text: '#ffffff',
  },
  {
    value: 'brown',
    label: 'Brown',
    fill: '#a16207',
    stroke: '#78350f',
    text: '#ffffff',
  },
]);

const ALT_PIN_CALLOUT_COLOR_ALIASES = Object.freeze({
  pink: 'pink',
  orange: 'orange',
  yellow: 'yellow',
  yellowgreen: 'yellow-green',
  'yellow-green': 'yellow-green',
  'yellow green': 'yellow-green',
  yellow_green: 'yellow-green',
  green: 'green',
  skyblue: 'sky-blue',
  'sky-blue': 'sky-blue',
  'sky blue': 'sky-blue',
  sky_blue: 'sky-blue',
  purple: 'purple',
  blue: 'blue',
  teal: 'teal',
  indigo: 'indigo',
  magenta: 'magenta',
  fuchsia: 'magenta',
  brown: 'brown',
});

const DEFAULT_ALT_PIN_CALLOUT_COLOR_RULES = Object.freeze([
  { pattern: /(^|[^a-z0-9])spi\d*(?=$|[^a-z0-9])/i, color: 'orange' },
  { pattern: /(^|[^a-z0-9])u(?:s)?art\d*(?=$|[^a-z0-9])/i, color: 'green' },
  { pattern: /(^|[^a-z0-9])i2c\d*(?=$|[^a-z0-9])/i, color: 'magenta' },
  { pattern: /(^|[^a-z0-9])pwm\d*(?=$|[^a-z0-9])/i, color: 'yellow' },
  { pattern: /(^|[^a-z0-9])pio\d*(?=$|[^a-z0-9])/i, color: 'blue' },
]);

const EMPTY_ALTERNATE_NAME_ENTRY = Object.freeze({
  name: '',
  x: '',
  y: '',
  color: '',
});

const getAlternateNameSourceValue = (pinOrValue) => (
  pinOrValue && typeof pinOrValue === 'object' && !Array.isArray(pinOrValue)
    ? pinOrValue.alternate_pin_names ?? pinOrValue.alternatePinNames ?? ''
    : pinOrValue
);

const normalizeAlternateName = (value) => String(value ?? '').trim();

const normalizeEditableCoordinate = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }

  const trimmed = String(value).trim();
  return trimmed;
};

const normalizeStoredCoordinate = (value) => {
  const trimmed = normalizeEditableCoordinate(value);

  if (!trimmed) {
    return null;
  }

  const numericValue = Number(trimmed);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const rotateRectPointAroundCenter = (x, y, centerX, centerY, rotationDeg) => {
  const radians = (Number(rotationDeg) * Math.PI) / 180;
  const offsetX = x - centerX;
  const offsetY = y - centerY;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return {
    x: centerX + (offsetX * cos) - (offsetY * sin),
    y: centerY + (offsetX * sin) + (offsetY * cos),
  };
};

const getRotatedRectBounds = ({ x, y, width, height, rotationDeg = 0 }) => {
  const normalizedRotation = Number(rotationDeg) || 0;

  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }

  if (normalizedRotation % 360 === 0) {
    return {
      minX: x,
      maxX: x + width,
      minY: y,
      maxY: y + height,
      centerX: x + (width / 2),
      centerY: y + (height / 2),
    };
  }

  const centerX = x + (width / 2);
  const centerY = y + (height / 2);
  const corners = [
    rotateRectPointAroundCenter(x, y, centerX, centerY, normalizedRotation),
    rotateRectPointAroundCenter(x + width, y, centerX, centerY, normalizedRotation),
    rotateRectPointAroundCenter(x, y + height, centerX, centerY, normalizedRotation),
    rotateRectPointAroundCenter(x + width, y + height, centerX, centerY, normalizedRotation),
  ];
  const xValues = corners.map((point) => point.x);
  const yValues = corners.map((point) => point.y);

  return {
    minX: Math.min(...xValues),
    maxX: Math.max(...xValues),
    minY: Math.min(...yValues),
    maxY: Math.max(...yValues),
    centerX,
    centerY,
  };
};

export const normalizeAlternatePinCalloutColor = (value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();
  return ALT_PIN_CALLOUT_COLOR_ALIASES[normalizedValue] || '';
};

export const getDefaultAlternatePinCalloutColor = (alternateName) => {
  const normalizedName = normalizeAlternateName(alternateName);

  if (!normalizedName) {
    return '';
  }

  const matchedRule = DEFAULT_ALT_PIN_CALLOUT_COLOR_RULES.find((rule) => (
    rule.pattern.test(normalizedName)
  ));

  return matchedRule?.color || '';
};

export const getAlternatePinCalloutColorOption = (value) => {
  const normalizedColor = normalizeAlternatePinCalloutColor(value);
  return ALT_PIN_CALLOUT_COLOR_OPTIONS.find((option) => option.value === normalizedColor) || null;
};

export const formatSuggestedAlternateNameCoordinate = (value) => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const roundedValue = Number(value.toFixed(2));
  return String(roundedValue);
};

export const getSuggestedAlternateNamePosition = ({
  pin,
  callout,
  index = 0,
  alternateNameEntries = [],
}) => {
  if (!pin || !callout) {
    return null;
  }

  const label = String(
    callout?.label
    ?? pin?.pin_name
    ?? pin?.pinName
    ?? pin?.pin_number
    ?? pin?.pinNumber
    ?? ''
  ).trim();
  const metrics = getCalloutMetrics(label, callout);
  const labelX = Number(callout?.label_x ?? callout?.labelX);
  const labelY = Number(callout?.label_y ?? callout?.labelY);
  const anchorX = Number(
    callout?.anchor_x
    ?? callout?.anchorX
    ?? pin?.position_x
    ?? pin?.position?.x
  );
  const rotationDeg = Number(callout?.rotation_deg ?? callout?.rotationDeg ?? 0);

  if (
    !Number.isFinite(labelX) ||
    !Number.isFinite(labelY) ||
    !Number.isFinite(metrics?.width) ||
    !Number.isFinite(metrics?.height) ||
    !Number.isFinite(metrics?.fontSize)
  ) {
    return null;
  }

  const bubbleBounds = getRotatedRectBounds({
    x: labelX,
    y: labelY,
    width: metrics.width,
    height: metrics.height,
    rotationDeg,
  });

  if (!bubbleBounds) {
    return null;
  }

  const connectorGap = Math.max(8, metrics.fontSize * 0.9);
  const outwardRight = !Number.isFinite(anchorX) || bubbleBounds.centerX >= anchorX;
  const baseSuggestedX = outwardRight
    ? bubbleBounds.maxX + connectorGap
    : bubbleBounds.minX - connectorGap - metrics.width;
  const firstEntryX = normalizeStoredCoordinate(alternateNameEntries?.[0]?.x);
  const resolvedBaseX = Number.isFinite(firstEntryX)
    ? firstEntryX
    : baseSuggestedX;
  const alignedY = bubbleBounds.centerY - (metrics.height / 2);
  const safeIndex = Math.max(0, Number(index) || 0);
  const stepDirection = resolvedBaseX > 0 ? 1 : -1;
  const steppedX = resolvedBaseX + (safeIndex * metrics.width * stepDirection);

  return {
    x: Number(steppedX.toFixed(2)),
    // Keep the default Y aligned with the first alternate callout so admins can
    // place a horizontal run of aliases without re-leveling each one by hand.
    y: Number(alignedY.toFixed(2)),
  };
};

const normalizeAlternateNameEntry = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const name = normalizeAlternateName(
      value.name
      ?? value.label
      ?? value.text
      ?? value.value
    );

    return {
      name,
      x: normalizeEditableCoordinate(
        value.x
        ?? value.label_x
        ?? value.labelX
      ),
      y: normalizeEditableCoordinate(
        value.y
        ?? value.label_y
        ?? value.labelY
      ),
      color: normalizeAlternatePinCalloutColor(
        value.color
        ?? value.callout_color
        ?? value.calloutColor
        ?? value.badge_color
        ?? value.badgeColor
      ) || getDefaultAlternatePinCalloutColor(name),
    };
  }

  const name = normalizeAlternateName(value);

  return {
    ...EMPTY_ALTERNATE_NAME_ENTRY,
    name,
    color: getDefaultAlternatePinCalloutColor(name),
  };
};

const collectAlternatePinNameEntries = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(collectAlternatePinNameEntries);
  }

  if (value && typeof value === 'object') {
    return [normalizeAlternateNameEntry(value)];
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
    || (trimmed.startsWith('{') && trimmed.endsWith('}'))
  ) {
    try {
      return collectAlternatePinNameEntries(JSON.parse(trimmed));
    } catch (error) {
      // Fall back to plain-text parsing when the value only looks like JSON.
    }
  }

  return trimmed
    .split(/\r?\n|[;,|]+/)
    .map((token) => normalizeAlternateNameEntry(token));
};

export const getEditablePinAlternateNameEntries = (
  pinOrValue,
  maxNames = MAX_PIN_ALTERNATE_NAMES
) => {
  const sourceValue = getAlternateNameSourceValue(pinOrValue);
  const alternateNameEntries = collectAlternatePinNameEntries(sourceValue).slice(0, maxNames);
  const paddedAlternateNameEntries = alternateNameEntries.map((entry) => ({
    ...EMPTY_ALTERNATE_NAME_ENTRY,
    ...entry,
  }));

  while (paddedAlternateNameEntries.length < maxNames) {
    paddedAlternateNameEntries.push({ ...EMPTY_ALTERNATE_NAME_ENTRY });
  }

  return paddedAlternateNameEntries;
};

export const getEditablePinAlternateNames = (pinOrValue, maxNames = MAX_PIN_ALTERNATE_NAMES) => (
  getEditablePinAlternateNameEntries(pinOrValue, maxNames).map((entry) => entry.name)
);

export const applySuggestedAlternateNameDefaults = (
  values,
  positionSuggestions = [],
  maxNames = MAX_PIN_ALTERNATE_NAMES
) => (
  getEditablePinAlternateNameEntries(values, maxNames).map((entry, index) => {
    const nextEntry = {
      ...EMPTY_ALTERNATE_NAME_ENTRY,
      ...entry,
      color: normalizeAlternatePinCalloutColor(entry?.color)
        || getDefaultAlternatePinCalloutColor(entry?.name),
    };

    if (!nextEntry.name) {
      return nextEntry;
    }

    const positionSuggestion = Array.isArray(positionSuggestions)
      ? positionSuggestions[index]
      : null;
    const suggestedX = formatSuggestedAlternateNameCoordinate(positionSuggestion?.x);
    const suggestedY = formatSuggestedAlternateNameCoordinate(positionSuggestion?.y);

    return {
      ...nextEntry,
      x: nextEntry.x || suggestedX,
      y: nextEntry.y || suggestedY,
    };
  })
);

export const serializePinAlternateNames = (values, maxNames = MAX_PIN_ALTERNATE_NAMES) => {
  const alternateNameEntries = collectAlternatePinNameEntries(values)
    .map((entry) => ({
      name: normalizeAlternateName(entry?.name),
      x: normalizeStoredCoordinate(entry?.x),
      y: normalizeStoredCoordinate(entry?.y),
      color: normalizeAlternatePinCalloutColor(entry?.color)
        || getDefaultAlternatePinCalloutColor(entry?.name),
    }))
    .filter((entry) => entry.name)
    .slice(0, maxNames);

  if (alternateNameEntries.length === 0) {
    return '';
  }

  return JSON.stringify(
    alternateNameEntries.map((entry) => {
      if (entry.x === null && entry.y === null && !entry.color) {
        return entry.name;
      }

      const serializedEntry = {
        name: entry.name,
      };

      if (entry.x !== null) {
        serializedEntry.x = entry.x;
      }

      if (entry.y !== null) {
        serializedEntry.y = entry.y;
      }

      if (entry.color) {
        serializedEntry.color = entry.color;
      }

      return serializedEntry;
    })
  );
};

export const getPinAlternateNameEntries = (pin) => {
  if (!pin || typeof pin !== 'object') {
    return [];
  }

  const primaryName = String(pin.pin_name ?? pin.pinName ?? '').trim().toLowerCase();
  const seen = new Set();

  return ALTERNATE_PIN_NAME_FIELDS
    .flatMap((fieldName) => collectAlternatePinNameEntries(pin[fieldName]))
    .map((entry) => ({
      name: normalizeAlternateName(entry?.name),
      x: normalizeStoredCoordinate(entry?.x),
      y: normalizeStoredCoordinate(entry?.y),
      color: normalizeAlternatePinCalloutColor(entry?.color),
    }))
    .filter((entry) => {
      if (!entry.name) {
        return false;
      }

      const dedupeKey = entry.name.toLowerCase();
      if (dedupeKey === primaryName || seen.has(dedupeKey)) {
        return false;
      }

      seen.add(dedupeKey);
      return true;
    });
};

export const getPinAlternateNames = (pin) => (
  getPinAlternateNameEntries(pin).map((entry) => entry.name)
);

export const formatPinAlternateNames = (pin, separator = ', ') => {
  const alternateNames = getPinAlternateNames(pin);
  return alternateNames.length ? alternateNames.join(separator) : '';
};
